<template>
    <v-sheet outlined>
        <v-toolbar flat class="ml-0">
            <v-toolbar-items>
                <PIconBtn icon="mdi-file" :tooltip="$t('new_voucher')" @click="handleNew" v-if="createPerm" />
                <PIconBtn icon="mdi-magnify" :tooltip="$t('search_by_number')" @click="mode.search = !mode.search" v-if="readPerm"/>
                <PIconBtn icon="mdi-content-save" :tooltip="$t('save')" color="primary" :disabled="mode.edit == false" @click="handleSave" v-if="createPerm || updatePerm"/>
                <PIconBtn icon="mdi-pencil" :tooltip="$t('edit')" :disabled="mode.edit == true || !can('update')" @click="handleEdit" v-if="updatePerm" />
                <!-- <PIconBtn icon="mdi-printer" tooltip="Print" @click="handlePrint"/> -->
                <PIconBtn icon="mdi-playlist-plus" :tooltip="$t('add_new_line')" :disabled="mode.edit == false" @click="addRow" v-if="createPerm || updatePerm"/>
                <PIconBtn icon="mdi-delete" :tooltip="$t('delete')" @click="handleDelete" :disabled="!form.id || !can('update')" v-if="deletePerm"/>
                <PIconBtn icon="mdi-form-select" color="primary" :tooltip="$t('simulate_entry')"  @click="handleSimulate" v-if="postPerm"/>
                <PIconBtn icon="mdi-clock" :tooltip="$t('draft_entries')"  @click="displayDrafts = true" v-if="postPerm"/>
                <v-divider vertical></v-divider>
            </v-toolbar-items>
            <v-toolbar-items>
                <PIconBtn icon="mdi-sync" :tooltip="$t('sync_data')" @click="syncData" :loading="syncLoading" />
            </v-toolbar-items>
            <v-divider vertical></v-divider>
        </v-toolbar>
        <v-divider></v-divider>
        
        <!-- Search toolbar -->
            
            <v-toolbar dense flat color="secondary" v-show="mode.search">
                <v-row no-gutters>
                    <v-col cols="3">
                        <div class="d-flex align-center">
                            <v-text-field
                                :label="$t('number')"
                                v-model="search"
                                hide-details
                                single-line
                                outlined
                                dense
                                background-color="white"
                                @keypress.enter="handleSearch"
                                >
                            </v-text-field>
                            <v-btn icon @click="handleSearch" :loading="loading.search">
                                <v-icon>
                                    mdi-magnify
                                </v-icon>
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>
                <v-toolbar-items>
                    <v-btn icon @click="mode.search = false">
                        <v-icon>mdi-menu-up</v-icon>
                    </v-btn>
                </v-toolbar-items>
            </v-toolbar>
            <v-divider v-show="mode.search"></v-divider>
        
        <div class="pa-4">
            <StockJournal
                ref="stockJournal" 
                :type="type"
                @mode="handleMode"
                @loading="handleLoading" 
                @change="handleFormChange" 
                @error="handleError"
                @success="handleSuccess"
                />
        </div>
        <PAlert v-bind="alert" v-model="alertShown"/>
        <LoadingDialog :value="loading.overlay" message="Please wait ..."/>
        <v-snackbar v-model="sb.display" :color="sb.color" bottom right>{{sb.text}}</v-snackbar>
        <simulate-entries
            v-if="simulateEntryShown"
            v-model="simulateEntryShown"
            :entry="simulatedEntry"
            :is-posted="isPosted"
            :can-post="canPost"
            @post="post"
            >
        </simulate-entries>
        <v-dialog :width="600" v-model="displayDrafts" overlay-opacity="0.2">
            <StockJournalDrafts
                v-if="displayDrafts"
                :drafts="drafts" 
                @stockJournalSelected="handleDraftSelect" 
                @close="displayDrafts = false"
                />
        </v-dialog>
    </v-sheet>
</template>

<script>
import permission from "./../../data/permissions"
import StockJournal from "./../../components/warehouse/StockJournal";
import PIconBtn from "./../../components/common/PIconBtn";
import PAlert from './../../components/common/PAlert';
import LoadingDialog from './../../components/common/LoadingDialog';
import SimulateEntries from './../../components/accounting/SimulateEntries';
import StockJournalDrafts from './../../components/warehouse/StockJournalDrafts'
import { mapGetters } from 'vuex';
import authorize from '../../utils/authorize';

export default {
    name: "StockJournalView",
    components: { StockJournal, PIconBtn, PAlert, LoadingDialog, SimulateEntries, StockJournalDrafts },
    props: {
        type: {
            required: true,
            type: String
        }
    },
    data() {
        return {
            openDialogDisplay: false,
            search: null,
            mode: {
                search: false
            },
            form: {},
            loading: {},
            changeCounter: 0,
            alertShown: false,
            alert: {
                title: "Are you sure",
                content: "Are you sure you want to do this action?"
            },
            sb: {
                display: false,
                color: "",
                text: ""
            },
            syncLoading: false,
            simulateEntryShown: false,
            simulatedEntry: null,
            displayDrafts: false,
            perm: permission
        }
    },
    computed: {
        isDirty() {
            return this.changeCounter > 10;
        },
        isPosted() {
            return this.form.state == 'POSTED';
        },
        canPost() {
            if(this.form.can) {
                return this.form.can.post;
            }
            return false;
        },
        readPerm() {
            return this.hasPermission(this.perm.stock_adjustment.read) || this.hasPermission(this.perm.good_receipt.read) || this.hasPermission(this.perm.good_issue.read)
        },
        updatePerm() {
            return this.hasPermission(this.perm.stock_adjustment.update) || this.hasPermission(this.perm.good_receipt.update) || this.hasPermission(this.perm.good_issue.update)
        },
        createPerm() {
            return this.hasPermission(this.perm.stock_adjustment.create) || this.hasPermission(this.perm.good_receipt.create) || this.hasPermission(this.perm.good_issue.create) 
        },
        deletePerm() {
            return this.hasPermission(this.perm.stock_adjustment.delete) || this.hasPermission(this.perm.good_receipt.delete) || this.hasPermission(this.perm.good_issue.delete)
        },
        postPerm() {
            return this.hasPermission(this.perm.stock_adjustment.post) || this.hasPermission(this.perm.good_receipt.post) || this.hasPermission(this.perm.good_issue.post)
        },
        ...mapGetters({
            drafts: 'stockJournal/drafts',
        })
    },
    methods: {
        hasPermission(code) {
            return authorize(code)
        },
        can(action) {
            return typeof this.form.can !== "undefined" ? !!this.form.can[action] : false;
        },
        addRow() {
            this.$refs.stockJournal.addRow();
        },
        handleSave() {
            this.alert.title = "Are you sure?";
            this.alert.content = "Are you sure you want to save this record?";
            this.alert.callback = this.save;
            this.alertShown = true;
        },
        save() {
            this.changeCounter = 0;  
            this.$refs.stockJournal.submit();
        },
        handleMode(value) {
            this.mode = {...this.mode, ...value};
        },
        handleEdit() {
            this.$refs.stockJournal.activateEditMode();
        },
        handleNew() {
            if(!this.isDirty) return this.new();
            this.alert.title = "Are you sure?";
            this.alert.content = "Unsaved changes will be discarded. Continue?"
            this.alert.callback = this.new;
            this.alertShown = true;
        },
        new() {  
            this.$refs.stockJournal.reset();
            this.$refs.stockJournal.fetchIdempotentKey();
            this.changeCounter = 0;
        },
        handleSearch() {
            if(!this.isDirty) return this.doSearch();
            this.alert = { title: "Are you sure?",  content: "Unsaved changes will be discarded. Continue?", callback: this.doSearch};
            this.alertShown = true;
        },
        doSearch() {  
            this.$refs.stockJournal.fetchStockJournalByNumber({number: this.search, type: this.type});
            this.changeCounter = 0;
        },
        handleDraftSelect(number) {
            this.search = number;
            this.displayDrafts = false;
            this.doSearch();
        },
        handleDelete() {
            this.alert = { title: "Are you sure?", content: "Are you sure you want to delete this record?", callback: this.delete };
            this.alertShown = true;
        },
        delete() {  
            this.$refs.stockJournal.delete();
        },
        handleFormChange(value) {
            this.form = value;
            this.changeCounter += 1;
        },
        handleError(message) {
            this.sb = {display: true, text: message, color: "error"}
        },
        handleLoading(loading) {
            this.loading = loading;
        },
        handleSuccess(message) {
            this.sb = {display: true, text: message, color: "success"}
            this.$refs.stockJournal.fetchIdempotentKey();
        },
        // handlePost() {
        //     this.alert = { title: "Confirm Posting", content: "Are you sure you want to post this record? This process cannot be undone.", callback: this.post };
        //     this.alertShown = true;
        // },
        post() {
            this.$refs.stockJournal.post();
            this.simulateEntryShown = false;
        },
        async syncData() {
            try {
                this.syncLoading = true;
                await Promise.all([
                    this.$store.dispatch('stockJournal/fetchUserPermissions'),
                    this.$store.dispatch('stockJournal/item/fetchItems'),
                    this.$store.dispatch('stockJournal/item/fetchWarehouses'),
                    this.$store.dispatch('stockJournal/item/fetchAccounts'),
                    this.$store.dispatch('stockJournal/item/fetchDimensions'),
                    this.$store.dispatch('stockJournal/fetchDrafts'),
                ])
                this.syncLoading = false;
                this.sb =  { color: "success", text: "Data Synced!", display: true};
            } catch (error) {
                this.syncLoading = false;
                let message = error.response ? error.response.data.message : error;
                this.snackbar =  { color: "danger", text: message, display: true };
            }
        },
        async handlePrint() {
            //
        },
        handleSimulate() {
            this.simulatedEntry = this.form.ledger_transaction;
            this.simulateEntryShown = true;
        },
        addShortcuts(e) {
            if(e.ctrlKey === true && e.keyCode === 83) {
                e.preventDefault();
                this.handleSave();
            }
            if(e.ctrlKey === true && e.altKey === true && e.keyCode === 78) {
                e.preventDefault();
                this.handleNew();
            }
            if(e.ctrlKey === true && e.keyCode === 79) {
                e.preventDefault();
                this.mode.search = !this.mode.search
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        if(!this.isDirty) next();
        this.alert = { title: "Are you sure?",  content: "Unsaved changes will be discarded. Continue?", callback: next};
        this.alertShown = true;
        next(false)
    },
    created() {
        document.addEventListener("keydown", this.addShortcuts);
        this.$store.dispatch('stockJournal/fetchDrafts', { type: this.type });
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.addShortcuts);
    }
};
</script>

<style></style>
